import { HHTextField } from '@hinge-health/react-component-library';
import Autocomplete from '@mui/material/Autocomplete';
import Typography from '@mui/material/Typography';
import { useGetValidContractsForHoldReleaseQuery } from '../../../../types';
import { BillTypeDataList } from '../../bill-grid-components/bill-management-types';

export interface ContractSelectProps {
  clientsInsurerId: number | undefined;
  selectedRows: BillTypeDataList;
  onSelectCallback: (contractId: number | undefined) => void;
}

export const CONTRACT_SELECT_LABEL = 'Contract';
export const NO_VALID_CONTRACTS_MESSAGE =
  'No valid contracts for the selected bills and client insurer.';

const getDateRange = (
  rows: BillTypeDataList,
): { minDate: string; maxDate: string } | null => {
  const dates = rows
    .map(r => r.dateOfService)
    .filter((d): d is string => Boolean(d));
  if (dates.length === 0) return null;
  const sorted = [...dates].sort((a, b) => a.localeCompare(b));
  return { minDate: sorted[0], maxDate: sorted[sorted.length - 1] };
};

export const ContractSelect = ({
  clientsInsurerId,
  selectedRows,
  onSelectCallback,
}: ContractSelectProps): JSX.Element => {
  const dateRange = getDateRange(selectedRows);

  const { data, loading, error } = useGetValidContractsForHoldReleaseQuery({
    variables: {
      clientsInsurerId: clientsInsurerId ?? 0,
      minDateOfService: dateRange?.minDate ?? '',
      maxDateOfService: dateRange?.maxDate ?? '',
    },
    skip: !clientsInsurerId || !dateRange,
  });

  const contracts = data?.getValidContractsForHoldRelease ?? [];

  const options = contracts.map(c => ({
    label: `ID: ${c.id} | ${c.billingType ?? 'N/A'} | ${c.startDate} – ${c.endDate ?? 'open'} | Partnership: ${c.partnershipId ?? 'N/A'}`,
    value: c.id,
  }));

  const handleChange = (
    newValue: { label: string; value: number } | null,
  ): void => {
    onSelectCallback(newValue?.value ?? undefined);
  };

  return (
    <>
      <Autocomplete
        disabled={!clientsInsurerId || !dateRange}
        options={options}
        size="small"
        isOptionEqualToValue={(option, value): boolean =>
          option.value === value.value
        }
        fullWidth
        onChange={(_, newValue): void => handleChange(newValue)}
        getOptionLabel={(option): string => option.label}
        renderInput={(params): JSX.Element => (
          <HHTextField
            {...params}
            hhVariant="variant-bypass"
            variant="outlined"
            label={CONTRACT_SELECT_LABEL}
            error={!!error}
            helperText={error ? error.message : ''}
          />
        )}
      />
      {clientsInsurerId &&
        dateRange &&
        contracts.length === 0 &&
        !loading &&
        !error && (
          <Typography variant="caption" color="error">
            {NO_VALID_CONTRACTS_MESSAGE}
          </Typography>
        )}
    </>
  );
};
